import { useRef, useEffect, useState } from 'react'
import { Heart, ShoppingBag } from 'lucide-react'
import type { GalleryAlbum } from '../types'

interface AlbumSelectorProps {
  albums: GalleryAlbum[]
  selectedAlbumId: string | null
  onSelect: (albumId: string | null) => void
  totalImageCount: number
  // Liked tab props
  likedCount: number
  showLikedOnly: boolean
  onToggleLiked: (show: boolean) => void
  // Purchased tab props
  purchasedCount: number
  showPurchasedOnly: boolean
  onTogglePurchased: (show: boolean) => void
}

export default function AlbumSelector({
  albums,
  selectedAlbumId,
  onSelect,
  totalImageCount,
  likedCount,
  showLikedOnly,
  onToggleLiked,
  purchasedCount,
  showPurchasedOnly,
  onTogglePurchased
}: AlbumSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeftGradient, setShowLeftGradient] = useState(false)
  const [showRightGradient, setShowRightGradient] = useState(false)

  // Check scroll position for gradients
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const checkScroll = () => {
      setShowLeftGradient(el.scrollLeft > 10)
      setShowRightGradient(el.scrollLeft < el.scrollWidth - el.clientWidth - 10)
    }

    checkScroll()
    el.addEventListener('scroll', checkScroll)
    window.addEventListener('resize', checkScroll)

    return () => {
      el.removeEventListener('scroll', checkScroll)
      window.removeEventListener('resize', checkScroll)
    }
  }, [albums])

  // Scroll selected album into view
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const selectedBtn = el.querySelector('[data-selected="true"]')
    if (selectedBtn) {
      selectedBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }, [selectedAlbumId])

  if (albums.length === 0) {
    return null
  }

  return (
    <div className="w-full z-50 bg-gray-900">
      <div className="bg-white/95 backdrop-blur-sm shadow-md relative overflow-hidden">
        {/* Left gradient */}
        {showLeftGradient && (
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white/95 to-transparent z-10 pointer-events-none" />
        )}

        {/* Right gradient */}
        {showRightGradient && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white/95 to-transparent z-10 pointer-events-none" />
        )}

        {/* Scrollable container */}
        <div
          ref={scrollRef}
          className="flex gap-2 p-2 overflow-x-auto scrollbar-hide scroll-smooth"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {/* All Photos */}
          <button
            onClick={() => {
              onToggleLiked(false)
              onTogglePurchased(false)
              onSelect(null)
            }}
            data-selected={selectedAlbumId === null && !showLikedOnly && !showPurchasedOnly}
            className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-w-fit ${
              selectedAlbumId === null && !showLikedOnly && !showPurchasedOnly
                ? 'bg-[#6b7850] text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95'
            }`}
          >
            All
            <span className={`ml-1.5 ${selectedAlbumId === null && !showLikedOnly && !showPurchasedOnly ? 'text-[#bab6a8]' : 'text-gray-500'}`}>
              {totalImageCount}
            </span>
          </button>

          {/* Liked Photos */}
          {likedCount > 0 && (
            <button
              onClick={() => {
                onToggleLiked(true)
                onTogglePurchased(false)
                onSelect(null)
              }}
              data-selected={showLikedOnly}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-w-fit flex items-center gap-1.5 ${
                showLikedOnly
                  ? 'bg-red-500 text-white shadow-md'
                  : 'bg-red-50 text-red-700 hover:bg-red-100 active:scale-95'
              }`}
            >
              <Heart size={14} className={showLikedOnly ? 'fill-white' : ''} />
              Liked
              <span className={`${showLikedOnly ? 'text-red-200' : 'text-red-400'}`}>
                {likedCount}
              </span>
            </button>
          )}

          {/* Unlocked Photos */}
          {purchasedCount > 0 && (
            <button
              onClick={() => {
                onTogglePurchased(true)
                onToggleLiked(false)
                onSelect(null)
              }}
              data-selected={showPurchasedOnly}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-w-fit flex items-center gap-1.5 ${
                showPurchasedOnly
                  ? 'bg-green-500 text-white shadow-md'
                  : 'bg-green-50 text-green-700 hover:bg-green-100 active:scale-95'
              }`}
            >
              <ShoppingBag size={14} />
              Unlocked
              <span className={`${showPurchasedOnly ? 'text-green-200' : 'text-green-400'}`}>
                {purchasedCount}
              </span>
            </button>
          )}

          {/* Album buttons */}
          {albums.map(album => (
            <button
              key={album.id}
              onClick={() => {
                onToggleLiked(false)
                onTogglePurchased(false)
                onSelect(album.id)
              }}
              data-selected={selectedAlbumId === album.id && !showLikedOnly && !showPurchasedOnly}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-w-fit ${
                selectedAlbumId === album.id
                  ? 'bg-[#6b7850] text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95'
              }`}
            >
              {album.name}
              <span className={`ml-1.5 ${selectedAlbumId === album.id ? 'text-[#bab6a8]' : 'text-gray-500'}`}>
                {album.image_count}
              </span>
            </button>
          ))}
        </div>

        {/* Hide scrollbar CSS */}
        <style>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    </div>
  )
}
